import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
export const CPF_DIGITS_REGEX = /^\d{11}$/;
export const CNPJ_DIGITS_REGEX = /^\d{14}$/;

export type BrazilianTaxIdKind = 'cpf' | 'cnpj';

export function onlyDigits(value: string): string {
  return value.replace(/\D/g, '');
}

export function isEmail(value: string): boolean {
  return EMAIL_REGEX.test(value.trim());
}

export function classifyBrazilianTaxId(
  value: string,
): BrazilianTaxIdKind | null {
  const digits = onlyDigits(value);

  if (CPF_DIGITS_REGEX.test(digits)) {
    return 'cpf';
  }

  if (CNPJ_DIGITS_REGEX.test(digits)) {
    return 'cnpj';
  }

  return null;
}

export function formatCpfProgressively(value: string): string {
  const digits = onlyDigits(value).slice(0, 11);

  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  }

  if (digits.length <= 9) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  }

  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

export function formatCnpjProgressively(value: string): string {
  const digits = onlyDigits(value).slice(0, 14);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 5) {
    return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  }

  if (digits.length <= 8) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  }

  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  }

  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

export function formatCpfOrCnpjProgressively(value: string): string {
  const digits = onlyDigits(value);

  if (digits.length <= 11) {
    return formatCpfProgressively(digits);
  }

  return formatCnpjProgressively(digits);
}

export function brazilianTaxIdShapeValidator(
  control: AbstractControl,
): ValidationErrors | null {
  if (typeof control.value !== 'string' || control.value.length === 0) {
    return null;
  }

  return classifyBrazilianTaxId(control.value)
    ? null
    : { brazilianTaxIdShape: true };
}

export const BRAZILIAN_TAX_ID_SHAPE_VALIDATOR: ValidatorFn =
  brazilianTaxIdShapeValidator;
